import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { List, ListItem, Chip, Button, CircularProgress, ListItemButton } from '@mui/material';
import { ChipContainer, Container, DisclaimerText, EmptyMessageText } from './styles';
import { SectionTitle } from '../Common/SectionTitle/SectionTitle';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '../../Hooks/useDebounce';
import { getSkills } from '../../Services/Skills';
import AutocompleteSelect from '../AutocompleteSelect/AutocompleteSelect';

interface SearchableChipBoxProps {
  initialSelectedChips: string[];
  handleSave: (selectedChips: string[]) => void;
  width?: string;
  preventEdit?: boolean;
}

const defaultWidth = '100%';

const SearchableChipBox = ({ initialSelectedChips, handleSave, width, preventEdit }: SearchableChipBoxProps) => {
  const [options, setOptions] = useState<string[]>([]);
  const [onEdit, setOnEdit] = useState<boolean>(false);
  const [selectedChips, setSelectedChips] = useState<string[]>(initialSelectedChips);
  const [skillsSearchTerm, setSkillsSearchTerm] = useState('');
  const debouncedSkillsSearchTerm = useDebounce(skillsSearchTerm, 200);

  const { data: skills, isLoading } = useQuery({
    queryKey: ['getSkills', debouncedSkillsSearchTerm],
    queryFn: () =>
      getSkills({
        page: 1,
        count: 10,
        name: debouncedSkillsSearchTerm || undefined,
      }),
    enabled: debouncedSkillsSearchTerm.length === 0 || debouncedSkillsSearchTerm.length > 2,
  });

  useEffect(() => {
    if (skills) {
      setOptions(skills?.items.map(skill => skill.name) ?? []);
    }
  }, [skills, debouncedSkillsSearchTerm]);

  useEffect(() => {
    setSelectedChips(initialSelectedChips);
  }, [initialSelectedChips]);

  const handleOptionSelect = (option: string) => {
    if (!selectedChips.includes(option)) {
      setSelectedChips((prev) => [...prev, option]);
    }
    setSkillsSearchTerm('');
    setOptions([]);
  };

  const handleChipDelete = (chip: string) => {
    setSelectedChips((prev) => prev.filter((chipx) => chipx !== chip));
  };

  const handleSaveClick = () => {
    let updatedChips = [...selectedChips];

    if (skillsSearchTerm.trim() && !selectedChips.includes(skillsSearchTerm.trim())) {
      updatedChips.push(skillsSearchTerm.trim());
    }

    const uniqueSelectedChips = Array.from(new Set(updatedChips));
    handleSave(uniqueSelectedChips);
    setSelectedChips(uniqueSelectedChips);
    setSkillsSearchTerm('');
    setOnEdit(false);
  };

  return (
    <Container width={width}>
      <SectionTitle
        title={t('common.skills')}
        handleEdit={!preventEdit ? () => setOnEdit(true) : undefined}
      />
      <ChipContainer>
        {!onEdit && (
          <>
            {selectedChips.length > 0 ? (
              selectedChips.map((chip) => (
                <Chip
                  key={chip}
                  label={chip}
                  onDelete={onEdit ? () => handleChipDelete(chip) : undefined}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))
            ) : (
              <EmptyMessageText>{t('actions.no_elem', { elem: 'skills' })}</EmptyMessageText>
            )}
          </>
        )}
        {onEdit && (
          <>
            <AutocompleteSelect
              searchTerm={skillsSearchTerm}
              onSearchTermChange={setSkillsSearchTerm}
              options={skills?.items.map(skill => skill.name) ?? []}
              selectedOptions={selectedChips}
              setSelectedOptions={setSelectedChips}
              width={defaultWidth}
              allowCustomValue={true}
            />
            <DisclaimerText>
              {t('common.skills_disclaimer')}
            </DisclaimerText>
            {!isLoading && options.length > 0 && !!skillsSearchTerm && (
              <List sx={{ maxHeight: 150, overflow: 'auto', border: '1px solid #ccc' }}>
                {options.map((option) => (
                  <ListItem key={option}>
                    <ListItemButton onClick={() => handleOptionSelect(option)}>
                      {option}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
            {isLoading && <CircularProgress />}

            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between' }}>
              <Button
                onClick={() => {
                  setSelectedChips(initialSelectedChips);
                  setOnEdit(false);
                }}
                variant="outlined"
                color="secondary"
                style={{ width: '48%' }}
              >
                {t('actions.cancel')}
              </Button>
              <Button
                onClick={handleSaveClick}
                variant="contained"
                color="primary"
                style={{ width: '48%' }}
              >
                {t('actions.save')}
              </Button>
            </div>
          </>
        )}
      </ChipContainer>
    </Container>
  );
};

export default SearchableChipBox;
